import { FunctionComponent, useRef } from 'react';
import { IPrismicCustomTypeContent } from './prismicTypes';
import {
  PrismicSliceAuthContext,
  getSliceByType,
  isFullPageSlice,
  isHeaderSlice,
  isMobilePageSlice,
} from './slices';
import { useAuthentication } from '@context/businessContainer';
import SkeletonLoader from '@components/SkeletonLoader';
import { ErrorBoundary } from 'react-error-boundary';
import logger from '@utils/logger';
import { useMediaQuery } from '@monofront/business/hooks';
import { isMobileApp, isRunningOnBrowser } from '@utils/environment';
import { EMPTY_OBJECT } from '@monofront/business/constants';
import { UnknownObject } from '@monofront/types';
import { useTagContextMatches } from '@hooks/businessContainer';

export enum SlicesContainerLayoutType {
  DEFAULT,
  INSIDE,
  CLEAR,
}
export interface ISlicesContainerProps {
  pageContents: IPrismicCustomTypeContent[];
  layout?: SlicesContainerLayoutType;
  data?: UnknownObject;
  UNSAFE_className?: string;
}

interface ISliceProps {
  prismicContent: IPrismicCustomTypeContent;
  position: number;
  props?: UnknownObject;
}

export const SlicesContainer: React.FC<ISlicesContainerProps> = ({
  pageContents,
  layout = SlicesContainerLayoutType.DEFAULT,
  data = EMPTY_OBJECT,
  UNSAFE_className = '',
}) => {
  const { isAuthenticated, isLoading, isMember } = useAuthentication();

  const isMobile = useMediaQuery(800);
  const isInsideLayout = layout === SlicesContainerLayoutType.INSIDE;
  const isClearLayout = layout === SlicesContainerLayoutType.CLEAR;

  const prevContent = useRef<unknown>(null);

  const { tagContextMatches } = useTagContextMatches({
    isWebMobile: !isMobileApp && isMobile,
  });

  return (
    <>
      <div className={`container ${UNSAFE_className}`}>
        {pageContents?.map(
          (prismicContent: IPrismicCustomTypeContent, index: number) => {
            if (
              prismicContent?.primary.tag_context &&
              !tagContextMatches(prismicContent.primary.tag_context)
            ) {
              return null;
            }

            const authContext = prismicContent.primary.auth_context;
            const { AUTHENTICATED, ANONYMOUS, MEMBER, REGISTERED } =
              PrismicSliceAuthContext;

            if (
              (authContext === AUTHENTICATED && Boolean(!isAuthenticated)) ||
              (authContext === ANONYMOUS && Boolean(isAuthenticated)) ||
              (authContext === MEMBER && !isMember) ||
              (authContext === REGISTERED &&
                (isMember || Boolean(!isAuthenticated)))
            ) {
              // @todo use user info stored on client side to display the loading or not (LF-2889)

              if (isLoading && isRunningOnBrowser) {
                return (
                  <SkeletonLoader key={index} height="300px" width="100%" />
                );
              }

              return null;
            }

            const Slice: FunctionComponent<ISliceProps> = getSliceByType(
              prismicContent.slice_type,
            );

            if (prismicContent.slice_type === 'MultipleProductsCarousels') {
              prevContent.current = prismicContent;
            }

            return !Slice ? null : (
              <div
                key={index}
                id={`slice-${prismicContent.slice_group_uid || index}`}
                className={`${
                  isHeaderSlice(prismicContent.slice_type)
                    ? 'header-slice'
                    : 'slice-container'
                }`}
              >
                <div
                  className={`${
                    isFullPageSlice(prismicContent.slice_type) ||
                    isInsideLayout ||
                    isClearLayout
                      ? ''
                      : 'limited-width-container slice-container-div'
                  } ${
                    isMobilePageSlice(prismicContent.slice_type)
                      ? 'full-mobile-width-slice'
                      : ''
                  }`}
                >
                  <ErrorBoundary
                    fallbackRender={() => null}
                    onError={(e) =>
                      logger.error(`Slice error ${prismicContent.slice_type}`, {
                        error: e,
                        sliceName: prismicContent.slice_type,
                      })
                    }
                  >
                    <Slice
                      prismicContent={prismicContent}
                      position={index}
                      props={data}
                    />
                  </ErrorBoundary>
                </div>
              </div>
            );
          },
        )}
      </div>
      <style jsx>{`
        .container {
          margin-top: ${isClearLayout
            ? '0px'
            : isInsideLayout
            ? `var(--space-2)`
            : `var(--space-8)`};
        }

        .slice-container {
          margin-bottom: ${isClearLayout
            ? '0px'
            : isInsideLayout
            ? `var(--space-2)`
            : `var(--space-8)`};
        }

        .header-slice {
          margin-bottom: var(--space-6);
        }

        @media (max-width: 800px) {
          .full-mobile-width-slice {
            padding: 0;
          }

          .slice-container-div {
            margin-left: var(--padding-layout-horizontal-base);
            margin-right: var(--padding-layout-horizontal-base);
          }
        }
      `}</style>
    </>
  );
};
